import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Redirect } from "react-router"
import firebase from "firebase"
import moment from "moment"

import { db } from "../../utils/firebase"
import { ModalContext } from "../../utils/providers/modal"
import "./mailbox.scss"

import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Badge from "../../components/ui/badge/badge"
import Button from "../../components/ui/button/button"
import Select from "../../components/ui/select/select"
import { LoadingIcon } from "../../utils/svgs"

import EmailRow from "./_components/email-row"
import EmailDrawer from "./_components/email-drawer"
import { runClassifierWithDefaults } from "./_components/ai-tools/run-classifier"

/**
 * Preset date ranges shown in the filter dropdown. Keys are stable identifiers;
 * values are the human-friendly labels that the Select component shows.
 */
const RANGE_OPTIONS = {
	today: "Today",
	yesterday: "Yesterday",
	this_week: "This week",
	this_month: "This month",
	last_month: "Last month",
}

/**
 * Resolve a range key to its inclusive `[fromMillis, toMillis]` window. We use
 * `isoWeek` so weeks start on Monday regardless of the user's locale, since
 * a sales week running Sun–Sat would be confusing in a UK business context.
 */
const rangeToWindow = (key) => {
	const now = moment()
	switch (key) {
		case "today":
			return [now.clone().startOf("day").valueOf(), now.clone().endOf("day").valueOf()]
		case "yesterday": {
			const yesterday = now.clone().subtract(1, "day")
			return [yesterday.startOf("day").valueOf(), yesterday.endOf("day").valueOf()]
		}
		case "this_week":
			return [
				now.clone().startOf("isoWeek").valueOf(),
				now.clone().endOf("isoWeek").valueOf(),
			]
		case "this_month":
			return [
				now.clone().startOf("month").valueOf(),
				now.clone().endOf("month").valueOf(),
			]
		case "last_month": {
			const lastMonth = now.clone().subtract(1, "month")
			return [lastMonth.startOf("month").valueOf(), lastMonth.endOf("month").valueOf()]
		}
		default:
			return [now.clone().startOf("isoWeek").valueOf(), now.clone().endOf("isoWeek").valueOf()]
	}
}

/**
 * Per-mailbox inbox page. Loads the `monitored_mailboxes/{id}` row for header
 * details, then queries the `communications` collection for emails received
 * by this mailbox between two dates.
 *
 * Match against `user_id` (the mailbox's Graph user GUID, not the Firestore
 * doc id of the mailbox row) since `communications.user_id` stores the GUID.
 *
 * Backed by the composite index `communications` (user_id ASC + occurred_at DESC)
 * declared in `firestore.indexes.json`.
 */
function MailboxPage({ mailboxID }) {
	const [mailbox, setMailbox] = useState(null)
	const [notFound, setNotFound] = useState(false)

	const [range, setRange] = useState("this_week")
	const [classification, setClassification] = useState("ALL")

	const [emails, setEmails] = useState([])
	const [loadingEmails, setLoadingEmails] = useState(true)

	const [selected, setSelected] = useState(null)

	const { showModal } = useContext(ModalContext)
	const [bulkProgress, setBulkProgress] = useState(null) // null | { done, total }
	// Snapshot listener replaces the `emails` array on every write, so we keep a
	// ref to the latest filtered list for the bulk loop to read between awaits
	// without re-triggering the loop on each tick.
	const filteredRef = useRef([])

	const [from, to] = useMemo(() => rangeToWindow(range), [range])

	// 1. Load the mailbox row
	useEffect(() => {
		db.doc(`monitored_mailboxes/${mailboxID}`)
			.get()
			.then((doc) => {
				if (!doc.exists) {
					setNotFound(true)
					return
				}
				setMailbox({ id: doc.id, ...doc.data() })
			})
			.catch((err) => {
				console.error("Failed to load mailbox", err)
				setNotFound(true)
			})
	}, [mailboxID])

	// 2. Subscribe to communications for this mailbox in the chosen window.
	//    A snapshot listener gives us live updates — new emails just appear in
	//    the table as they're ingested.
	useEffect(() => {
		if (!mailbox?.graph_user_id) return

		setLoadingEmails(true)

		const fromTs = firebase.firestore.Timestamp.fromMillis(from)
		const toTs = firebase.firestore.Timestamp.fromMillis(to)

		const unsubscribe = db
			.collection("communications")
			.where("user_id", "==", mailbox.graph_user_id)
			.where("occurred_at", ">=", fromTs)
			.where("occurred_at", "<=", toTs)
			.orderBy("occurred_at", "desc")
			.limit(500)
			.onSnapshot(
				(snapshot) => {
					const next = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
					setEmails(next)
					setLoadingEmails(false)
				},
				(err) => {
					console.error("Failed to subscribe to communications", err)
					setLoadingEmails(false)
				}
			)

		return () => unsubscribe()
	}, [mailbox?.graph_user_id, from, to])

	// Build the classification filter options from the emails currently loaded.
	// "ALL" is always present; "UNCLASSIFIED" is shown if any email lacks a real
	// classification. Real classification keys are sorted alphabetically.
	const classificationOptions = useMemo(() => {
		const options = { ALL: "All classifications" }
		let hasUnclassified = false
		const real = new Set()
		for (const comm of emails) {
			const value = comm.classification
			if (!value || value === "UNCLASSIFIED") {
				hasUnclassified = true
			} else {
				real.add(value)
			}
		}
		if (hasUnclassified) options.UNCLASSIFIED = "Unclassified"
		Array.from(real)
			.sort()
			.forEach((key) => {
				options[key] = key
			})
		return options
	}, [emails])

	const filteredEmails = useMemo(() => {
		if (classification === "ALL") return emails
		if (classification === "UNCLASSIFIED") {
			return emails.filter(
				(comm) => !comm.classification || comm.classification === "UNCLASSIFIED"
			)
		}
		return emails.filter((comm) => comm.classification === classification)
	}, [emails, classification])

	filteredRef.current = filteredEmails

	// Sequentially classify every email currently in the filtered view. We
	// snapshot the id list up front so emails arriving via the live listener
	// after the user clicks "Classify all" don't keep extending the run.
	const classifyAllInView = async () => {
		const ids = filteredRef.current.map((comm) => comm.id)
		if (ids.length === 0) return

		setBulkProgress({ done: 0, total: ids.length })
		const failures = []

		for (let i = 0; i < ids.length; i++) {
			try {
				await runClassifierWithDefaults(ids[i])
			} catch (err) {
				failures.push({ id: ids[i], message: err?.message || "Unknown error" })
			}
			setBulkProgress({ done: i + 1, total: ids.length })
		}

		setBulkProgress(null)

		if (failures.length > 0) {
			showModal({
				type: "ALERT",
				title: "Bulk classification finished with errors",
				body:
					`Classified ${ids.length - failures.length} of ${ids.length} email${ids.length === 1 ? "" : "s"}. ` +
					`${failures.length} failed:\n\n` +
					failures.slice(0, 5).map((f) => `• ${f.id}: ${f.message}`).join("\n") +
					(failures.length > 5 ? `\n…and ${failures.length - 5} more` : ""),
				cancel: { label: "Dismiss", action: () => null },
				next: { label: "Dismiss", action: () => null },
			})
		}
	}

	const confirmClassifyAll = () => {
		const total = filteredRef.current.length
		if (total === 0) return
		showModal({
			type: "ALERT",
			title: `Classify ${total} email${total === 1 ? "" : "s"}?`,
			body:
				`This re-runs the classifier against every email in the current view, one at a time. ` +
				`It can take a while for large ranges and will replace any existing classification on these emails.`,
			cancel: { label: "Cancel", action: () => null },
			next: { label: `Yes, classify ${total}`, action: () => classifyAllInView() },
		})
	}

	if (notFound) return <Redirect to="/communications/mailboxes" />
	if (!mailbox) {
		return (
			<Tile fullPage={true}>
				<Title>
					<h1>Loading mailbox…</h1>
				</Title>
			</Tile>
		)
	}

	const lastReceivedLabel = mailbox.last_message_at?.seconds
		? moment.unix(mailbox.last_message_at.seconds).fromNow()
		: "Never"

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<div className="mailbox-page-heading">
					<h1>{mailbox.display_name}</h1>
					<small className="mailbox-page-subline">
						<span>{mailbox.email}</span>
						<span className="dot">•</span>
						<StatusBadge status={mailbox.status} />
						<span className="dot">•</span>
						<span>Last email {lastReceivedLabel}</span>
					</small>
				</div>
			</Title>

			<div className="mailbox-page-filters">
				<Select
					showLabel
					placeholder="Date range:"
					value={range}
					selected={range}
					onSelect={(option) => setRange(option.option)}
					options={RANGE_OPTIONS}
				/>
				<Select
					showLabel
					placeholder="Classification:"
					value={classification}
					selected={classification}
					onSelect={(option) => setClassification(option.option)}
					options={classificationOptions}
				/>
				<small className="mailbox-page-count">
					{classification === "ALL"
						? `${filteredEmails.length} email${filteredEmails.length === 1 ? "" : "s"} in range`
						: `${filteredEmails.length} of ${emails.length} email${emails.length === 1 ? "" : "s"} in range`}
				</small>
				<div className="mailbox-page-bulk-classify">
					<Button
						small={true}
						label={
							bulkProgress
								? `Classifying ${bulkProgress.done} / ${bulkProgress.total}`
								: "Classify all in view"
						}
						loading={bulkProgress !== null}
						loadingText={
							bulkProgress
								? `Classifying ${bulkProgress.done} / ${bulkProgress.total}`
								: "Classifying..."
						}
						disabled={bulkProgress !== null || filteredEmails.length === 0}
						onClick={confirmClassifyAll}
					/>
				</div>
			</div>

			<Table
				className="mailbox-emails-table"
				headings={["Received", "From", "Subject", "Classification", "Confidence", "Attachment(s)", ""]}
				noResults={!loadingEmails && filteredEmails.length === 0}
				noResultsMessage={
					emails.length === 0
						? "No emails received in this date range"
						: "No emails match this classification"
				}>
				{filteredEmails.map((comm) => (
					<EmailRow
						key={comm.id}
						comm={comm}
						onSelect={(c) => setSelected(c)}
					/>
				))}

				{loadingEmails && (
					<div className="table-loading-splash">
						<div className="loading-wrapper">
							<p>Loading...</p>
							<div className="svg-loading-wrap">
								<LoadingIcon />
							</div>
						</div>
					</div>
				)}
			</Table>

			{selected && (
				<EmailDrawer
					comm={selected}
					close={() => setSelected(null)}
				/>
			)}
		</Tile>
	)
}

function StatusBadge({ status }) {
	if (status === "ACTIVE") return <Badge type="POSITIVE" label="Active" />
	if (status === "PAUSED") return <Badge type="INFO" label="Paused" />
	if (status === "ERROR") return <Badge type="NEGATIVE" label="Error" />
	return <Badge label={status || "Unknown"} />
}

/**
 * Wrapper to pull the route parameter and forward to the page-level component.
 * Mirrors the `EnquiryPageWrap` / `BookingWrap` pattern used elsewhere.
 */
function MailboxPageWrap(props) {
	const { mailboxID } = props.match.params
	return <MailboxPage mailboxID={mailboxID} />
}

export default MailboxPageWrap
